import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { key: 1 }

import {
  IonApp,
  IonContent,
  IonFooter,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonMenu,
  IonMenuToggle,
  IonNote,
  IonRouterOutlet,
  IonSplitPane,
  IonText,
} from '@ionic/vue'
import {
  checkmarkCircleOutline,
  checkmarkCircleSharp,
  footballOutline,
  footballSharp,
  homeOutline,
  homeSharp,
  informationCircleOutline,
  informationCircleSharp,
  optionsOutline,
  optionsSharp,
  peopleCircleOutline,
  peopleCircleSharp,
  peopleOutline,
  peopleSharp,
  personAddOutline,
  personAddSharp,
  personCircleOutline,
  personCircleSharp,
  trophyOutline,
  trophySharp,
} from 'ionicons/icons'
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAppConfig, useAppSettings } from './composables/app'
import { useCurrentUserProfile } from './composables/userProfile'
import { USER_ROLES } from './constants'
import { getUserName } from './utils/userProfile'


export default /*@__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

const router = useRouter()
const route = useRoute()
const appSettings = useAppSettings()
const appConfig = useAppConfig()
const userProfile = useCurrentUserProfile()

const name = computed(() => {
  if (!userProfile.value) return 'undefined'
  return getUserName(userProfile)
})

const isSelected = (url: string) => url === route.path

const checkScoresPage = {
  title: 'Check Scores',
  url: '/check-scores',
  iosIcon: checkmarkCircleOutline,
  mdIcon: checkmarkCircleSharp,
  color: 'success',
}
const rankingPage = {
  title: 'Classement',
  url: '/ranking',
  iosIcon: trophyOutline,
  mdIcon: trophySharp,
  color: 'warning',
}
const settingsPage = {
  title: 'Paramètres',
  url: '/settings',
  iosIcon: optionsOutline,
  mdIcon: optionsSharp,
  color: 'primary',
}
const homePage = {
  title: 'Accueil',
  url: '/home',
  iosIcon: homeOutline,
  mdIcon: homeSharp,
  color: 'primary',
}
const guestHomePage = {
  title: 'Accueil',
  url: '/guest',
  iosIcon: homeOutline,
  mdIcon: homeSharp,
  color: 'primary',
}
const loginPage = {
  title: 'Connexion',
  url: '/login',
  iosIcon: personCircleOutline,
  mdIcon: personCircleSharp,
  color: 'primary',
}
const profilePage = {
  title: 'Profil',
  url: '/profile',
  iosIcon: personCircleOutline,
  mdIcon: personCircleSharp,
  color: 'primary',
}
const aboutPage = {
  title: 'A propos',
  url: '/about',
  iosIcon: informationCircleOutline,
  mdIcon: informationCircleSharp,
  color: 'medium',
}
const playerGroupsPage = {
  title: 'Joueurs',
  url: '/player-group',
  iosIcon: peopleOutline,
  mdIcon: peopleOutline,
  color: 'success',
}
const attendantGroupsPage = {
  title: 'Animateurs',
  url: '/attendant-group',
  iosIcon: peopleOutline,
  mdIcon: peopleOutline,
  color: 'danger',
}
const gamesPage = {
  title: 'Épreuves',
  url: '/games',
  iosIcon: footballOutline,
  mdIcon: footballSharp,
  color: 'ternary',
}
const applicantsPage = {
  title: 'Demandes d\'accès',
  url: '/applicants',
  iosIcon: personAddOutline,
  mdIcon: personAddSharp,
  color: 'ternary',
}
const appPages = computed(() => {
  if (!userProfile.value) return [guestHomePage, loginPage, aboutPage]
  let pages = [homePage]
  if (userProfile.value.teamId) {
    pages = [
      ...pages,
      {
        title: 'Mon Equipe',
        url: `/team/${userProfile.value.teamId}`,
        iosIcon: peopleCircleOutline,
        mdIcon: peopleCircleSharp,
        color: 'success',
      },
    ]
  }
  if (userProfile.value.role >= USER_ROLES.Animateur) {
    if (userProfile.value.games && appConfig.value) {
      for (const timeSlot of appConfig.value.attendantSchedule) {
        if (!userProfile.value.games[timeSlot.id]) continue
        pages = [
          ...pages,
          {
            title: `Mon épreuve (${timeSlot.name})`,
            url: `/game/${userProfile.value.games[timeSlot.id].id}`,
            iosIcon: footballOutline,
            mdIcon: footballSharp,
            color: 'secondary',
          },
        ]
      }
    }
    if (userProfile.value.groupId) {
      pages = [
        ...pages,
        {
          title: 'Ma section',
          url: `/attendant-group/${userProfile.value.groupId}`,
          iosIcon: peopleSharp,
          mdIcon: peopleSharp,
          color: 'secondary',
        },
      ]
    }
  }
  if (userProfile.value.role === USER_ROLES.Participant) {
    pages = [
      ...pages,
      {
        title: 'Ma section',
        url: `/player-group/${userProfile.value.groupId}`,
        iosIcon: peopleSharp,
        mdIcon: peopleSharp,
        color: 'secondary',
      },
    ]
  }
  if (userProfile.value.role >= USER_ROLES.Organisateur) pages = [...pages, checkScoresPage]
  if (userProfile.value.role > USER_ROLES.Newbie) pages = [...pages, gamesPage, playerGroupsPage]
  if (userProfile.value.role >= USER_ROLES.Animateur) pages = [...pages, attendantGroupsPage]
  if (userProfile.value.role >= USER_ROLES.Chef) pages = [...pages, applicantsPage]
  if (appSettings.value?.isRankingPublic || userProfile.value.role >= USER_ROLES.Administrateur)
    pages = [...pages, rankingPage]
  if (userProfile.value.role >= USER_ROLES.Administrateur) pages = [...pages, settingsPage]
  // bottom pages
  pages = [...pages, profilePage, aboutPage]
  return pages
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(IonApp), null, {
    default: _withCtx(() => [
      _createVNode(_unref(IonSplitPane), { "content-id": "main-content" }, {
        default: _withCtx(() => [
          _createVNode(_unref(IonMenu), {
            "content-id": "main-content",
            type: "overlay",
            side: "start",
            "swipe-gesture": false
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(IonContent), { style: {"height":"100%"} }, {
                default: _withCtx(() => [
                  _createVNode(_unref(IonList), { id: "menu-list" }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(IonListHeader), null, {
                        default: _withCtx(() => _cache[1] || (_cache[1] = [
                          _createTextVNode("Baden Battle")
                        ])),
                        _: 1
                      }),
                      _createVNode(_unref(IonNote), { class: "ion-text-uppercase" }, {
                        default: _withCtx(() => _cache[2] || (_cache[2] = [
                          _createTextVNode(" score app ")
                        ])),
                        _: 1
                      }),
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(appPages.value, (p, i) => {
                        return (_openBlock(), _createBlock(_unref(IonMenuToggle), {
                          key: i,
                          "auto-hide": false
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_unref(IonItem), {
                              "router-direction": "root",
                              "router-link": p.url,
                              lines: "none",
                              detail: false,
                              class: _normalizeClass(["hydrated", { selected: isSelected(p.url) }])
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_unref(IonIcon), {
                                  slot: "start",
                                  ios: p.iosIcon,
                                  md: p.mdIcon,
                                  color: p.color ?? 'medium'
                                }, null, 8, ["ios", "md", "color"]),
                                _createVNode(_unref(IonLabel), null, {
                                  default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(p.title), 1)
                                  ]),
                                  _: 2
                                }, 1024)
                              ]),
                              _: 2
                            }, 1032, ["router-link", "class"])
                          ]),
                          _: 2
                        }, 1024))
                      }), 128))
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createVNode(_unref(IonMenuToggle), { "auto-hide": false }, {
                default: _withCtx(() => [
                  _createVNode(_unref(IonFooter), {
                    collapse: "fade",
                    class: "ion-padding",
                    onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(router).replace('/profile')))
                  }, {
                    default: _withCtx(() => [
                      (_unref(userProfile))
                        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                            _createVNode(_unref(IonText), null, {
                              default: _withCtx(() => [
                                _createTextVNode("Connecté en tant que " + _toDisplayString(name.value), 1)
                              ]),
                              _: 1
                            })
                          ]))
                        : (_openBlock(), _createElementBlock("div", _hoisted_2, [
                            _createVNode(_unref(IonText), null, {
                              default: _withCtx(() => _cache[3] || (_cache[3] = [
                                _createTextVNode("Pas authentifié")
                              ])),
                              _: 1
                            })
                          ]))
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_unref(IonRouterOutlet), { id: "main-content" })
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})